"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import {
  AlertCircle,
  User,
  Mail,
  Ticket,
  Loader2,
  CheckCircle,
  X,
  Plus,
  Minus,
} from "lucide-react";
import {
  BookingStep,
  VoucherRecipient,
  VoucherPurchaseInput,
} from "@/types/booking";
import { useState, useEffect } from "react";
import { useLazySearchClient } from "@/lib/apollo/hooks/booking-edge";
import SearchableCountrySelect from "@/components/ui/SearchableCountrySelect";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { formatPrice, getBusinessCurrency } from "@/lib/utils";
import CartSummary from "../CartSummary";
import { useCreateVoucherPurchase } from "@/lib/apollo/hooks/booking";
import { PaymentProviderModal } from "@/components/booking/PaymentProviderModal";
import { StripeCheckoutModal } from "@/components/booking/StripeCheckoutModal";
import {
  getPaymentOptionConfig,
  getPaymentOptionsForCountry,
  type PaymentOption,
  type PaymentProvider,
} from "@/lib/payment-providers";

export default function VoucherCheckout() {
  useScrollToTop();
  const {
    selectedSalon,
    selectedPackages,
    businessInfo,
    serviceClient,
    resetBooking,
    handleNextStep,
    addToast,
    modifyPackageInCart,
    isMobile,
  } = useBooking();

  const [loading, setLoading] = useState(false);
  const [createVoucherPurchase] = useCreateVoucherPurchase();
  const [paymentProvider, setPaymentProvider] =
    useState<PaymentProvider>("paystack");
  const [paystackLoaded, setPaystackLoaded] = useState(false);
  const [pocketLoaded, setPocketLoaded] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripePaymentReference, setStripePaymentReference] = useState<string | null>(null);
  const paymentCountryCode = selectedSalon?.countryCode || businessInfo?.countryCode;
  const availablePaymentOptions = getPaymentOptionsForCountry(paymentCountryCode);
  const currency = getBusinessCurrency(businessInfo);
  const [searchLoading, setSearchLoading] = useState<{
    [key: number]: boolean;
  }>({});
  const [showPersonalInfo, setShowPersonalInfo] = useState<{
    [key: number]: boolean;
  }>({});

  const [recipients, setRecipients] = useState<VoucherRecipient[]>(
    selectedPackages.map((pkg) => ({
      firstName: serviceClient?.firstName || "",
      lastName: serviceClient?.lastName || "",
      email: serviceClient?.email || "",
      countryCode:
        serviceClient?.countryCode || selectedSalon?.countryCode || "NG",
      callingCode:
        serviceClient?.callingCode || selectedSalon?.callingCode || "+234",
      phone: serviceClient?.phone || "",
      packages: [
        {
          packageId: pkg.id,
          quantity: pkg.quantity || 1,
        },
      ],
    })),
  );

  const [formErrors, setFormErrors] = useState<any>({});

  // GraphQL query for searching client
  const [searchClientQuery] = useLazySearchClient();

  // Load both payment provider scripts on mount
  useEffect(() => {
    // Load Paystack (only if not already loaded)
    if (!document.querySelector('script[src*="js.paystack.co"]')) {
      const paystackScript = document.createElement("script");
      paystackScript.src = "https://js.paystack.co/v2/inline.js";
      paystackScript.async = true;
      paystackScript.onload = () => setPaystackLoaded(true);
      document.body.appendChild(paystackScript);
    } else if (window.PaystackPop) {
      setPaystackLoaded(true);
    }

    // Load Pocket (only if not already loaded)
    if (!document.querySelector('script[src*="pocket-checkout-sdk"]')) {
      const pocketScript = document.createElement("script");
      pocketScript.src =
        "https://pocket-checkout-sdk.netlify.app/checkout-sdk.js";
      pocketScript.async = true;
      pocketScript.onload = () => setPocketLoaded(true);
      document.body.appendChild(pocketScript);
    } else if ((window as any).Pocket) {
      setPocketLoaded(true);
    }
  }, []);

  // Helper function to distribute quantity across recipients
  const distributeQuantity = (totalQuantity: number, numRecipients: number) => {
    const baseQuantity = Math.floor(totalQuantity / numRecipients);
    const remainder = totalQuantity % numRecipients;

    const distribution = Array(numRecipients).fill(baseQuantity);
    // Give the remainder to the first recipient
    if (remainder > 0) {
      distribution[0] += remainder;
    }

    return distribution;
  };

  // Helper function to get recipients for a specific package
  const getRecipientsForPackage = (packageId: string) => {
    return recipients.filter((recipient) =>
      recipient.packages.some((pkg) => pkg.packageId === packageId),
    );
  };

  // Update recipients when selectedPackages change (preserve existing data)
  useEffect(() => {
    if (selectedPackages.length > 0) {
      setRecipients((prevRecipients) => {
        const updatedRecipients = [...prevRecipients];

        selectedPackages.forEach((pkg) => {
          const existingPackageRecipients = updatedRecipients.filter(
            (recipient) =>
              recipient.packages.some((p) => p.packageId === pkg.id),
          );

          if (existingPackageRecipients.length === 0) {
            // Create first recipient for new package
            updatedRecipients.push({
              firstName: serviceClient?.firstName || "",
              lastName: serviceClient?.lastName || "",
              email: serviceClient?.email || "",
              countryCode:
                serviceClient?.countryCode ||
                selectedSalon?.countryCode ||
                "NG",
              callingCode:
                serviceClient?.callingCode ||
                selectedSalon?.callingCode ||
                "+234",
              phone: serviceClient?.phone || "",
              packages: [
                {
                  packageId: pkg.id,
                  quantity: pkg.quantity || 1,
                },
              ],
            });
          } else {
            // Update quantities for existing recipients based on new total quantity
            const totalQuantity = pkg.quantity || 1;

            // If quantity decreased, remove excess recipients (keep the first ones)
            if (existingPackageRecipients.length > totalQuantity) {
              const recipientsToRemove =
                existingPackageRecipients.slice(totalQuantity);
              recipientsToRemove.forEach((recipientToRemove) => {
                const indexToRemove = updatedRecipients.findIndex(
                  (r) => r === recipientToRemove,
                );
                if (indexToRemove !== -1) {
                  updatedRecipients.splice(indexToRemove, 1);
                }
              });
              // Update the list after removal
              const remainingPackageRecipients = updatedRecipients.filter(
                (recipient) =>
                  recipient.packages.some((p) => p.packageId === pkg.id),
              );
              const quantityDistribution = distributeQuantity(
                totalQuantity,
                remainingPackageRecipients.length,
              );

              remainingPackageRecipients.forEach((recipient, index) => {
                const recipientIndex = updatedRecipients.findIndex(
                  (r) => r === recipient,
                );
                if (recipientIndex !== -1) {
                  updatedRecipients[recipientIndex] = {
                    ...updatedRecipients[recipientIndex],
                    packages: updatedRecipients[recipientIndex].packages.map(
                      (p) =>
                        p.packageId === pkg.id
                          ? { ...p, quantity: quantityDistribution[index] }
                          : p,
                    ),
                  };
                }
              });
            } else {
              // Just redistribute quantities among existing recipients
              const quantityDistribution = distributeQuantity(
                totalQuantity,
                existingPackageRecipients.length,
              );

              existingPackageRecipients.forEach((recipient, index) => {
                const recipientIndex = updatedRecipients.findIndex(
                  (r) => r === recipient,
                );
                if (recipientIndex !== -1) {
                  updatedRecipients[recipientIndex] = {
                    ...updatedRecipients[recipientIndex],
                    packages: updatedRecipients[recipientIndex].packages.map(
                      (p) =>
                        p.packageId === pkg.id
                          ? { ...p, quantity: quantityDistribution[index] }
                          : p,
                    ),
                  };
                }
              });
            }
          }
        });

        // Remove recipients for packages that no longer exist
        return updatedRecipients.filter((recipient) =>
          recipient.packages.some((pkg) =>
            selectedPackages.some(
              (selectedPkg) => selectedPkg.id === pkg.packageId,
            ),
          ),
        );
      });
    }
  }, [selectedPackages, selectedSalon]);

  // Validate all recipients
  const validateForm = () => {
    const newErrors: any = {};

    recipients.forEach((recipient, index) => {
      const recipientErrors: any = {};

      // Validate phone
      const cleanPhone = recipient.phone.replace(/[^0-9]/g, "");
      if (!cleanPhone) {
        recipientErrors.phone = "Phone number is required";
      } else if (cleanPhone.length < 5) {
        recipientErrors.phone = "Please enter a valid phone number";
      }

      // Only validate personal info if it's being shown
      if (showPersonalInfo[index]) {
        // Validate first name
        if (!recipient.firstName.trim()) {
          recipientErrors.firstName = "First name is required";
        }

        // Validate last name
        if (!recipient.lastName.trim()) {
          recipientErrors.lastName = "Last name is required";
        }

        // Validate email
        if (!recipient.email.trim()) {
          recipientErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.email)) {
          recipientErrors.email = "Please enter a valid email";
        }
      }

      // Validate packages
      if (!recipient.packages.length) {
        recipientErrors.packages = "At least one voucher must be selected";
      }

      if (Object.keys(recipientErrors).length > 0) {
        newErrors[`recipient_${index}`] = recipientErrors;
      }
    });

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePaymentSuccess = async (reference: string) => {
    setLoading(false);
    setShowSuccessDialog(true);
  };

  const handlePaymentClose = () => {
    addToast({
      message: "Payment cancelled",
      variant: "error",
    });
    setLoading(false);
  };

  const initiatePaystackPayment = async (responseData: any, channels?: string[]) => {
    if (!window.PaystackPop) {
      console.error("Paystack not loaded");
      addToast({
        message: "Payment system not ready. Please try again.",
        variant: "error",
      });
      setLoading(false);
      return;
    }

    const paymentAuth = responseData.paymentAuthorization;
    if (!paymentAuth?.accessCode) {
      addToast({
        message: "Payment authorization failed. Please try again.",
        variant: "error",
      });
      setLoading(false);
      return;
    }

    const paystackKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;
    if (!paystackKey) {
      addToast({
        message: "Payment system not configured. Please contact support.",
        variant: "error",
      });
      setLoading(false);
      return;
    }

    try {
      // Use Paystack v2 checkout method for Apple Pay support
      // This method automatically detects iOS/Safari and shows Apple Pay option
      const paystackPop = new window.PaystackPop();
      
      // Use the backend-calculated totalAmountPayable (includes correct processing fees, taxes, discounts)
      const totalAmount = responseData.totalAmountPayable;

      // Get first recipient's email for the payment
      const recipientEmail = recipients[0]?.email || "";

      await paystackPop.checkout({
        key: paystackKey,
        email: recipientEmail,
        amount: totalAmount ? Math.round(totalAmount * currency.factor) : 0,
        currency: currency.code,
        accessCode: paymentAuth.accessCode,
        ...(channels && { channels }),
        onSuccess: (transaction: { reference: string }) => {
          handlePaymentSuccess(transaction.reference);
        },
        onCancel: () => {
          handlePaymentClose();
        },
        onClose: () => {
          handlePaymentClose();
        },
      });
    } catch (error) {
      console.error("Paystack checkout error:", error);
      addToast({
        message: "Failed to initiate payment. Please try again.",
        variant: "error",
      });
      setLoading(false);
    }
  };

  const initiatePocketPayment = (responseData: any) => {
    const Pocket = (window as any).Pocket;
    if (!Pocket) {
      console.error("Pocket SDK not loaded");
      addToast({
        message: "Payment system not ready. Please try again.",
        variant: "error",
      });
      setLoading(false);
      return;
    }

    const pocketKey = import.meta.env.VITE_POCKET_PUBLIC_KEY;
    if (!pocketKey) {
      addToast({
        message: "Payment system not configured. Please contact support.",
        variant: "error",
      });
      setLoading(false);
      return;
    }

    // Use the backend-calculated totalAmountPayable (includes correct processing fees, taxes, discounts)
    const totalAmount = responseData.totalAmountPayable;

    if (!totalAmount) {
      addToast({
        message: "Could not determine payment amount. Please try again.",
        variant: "error",
      });
      setLoading(false);
      return;
    }

    // Generate a unique reference for Pocket (used as pvb_destination_account_creation_reference)
    const reference = `PKT-VCHR-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    try {
      // Initialize Pocket checkout with popup view
      const pocketBusinessId = import.meta.env.VITE_POCKET_BUSINESS_ID;
      const pocketWalletId = import.meta.env.VITE_POCKET_WALLET_ID;

      const packageNames = selectedPackages.map((pkg) => pkg.name).join(", ");

      const pocketConfig: any = {
        key: pocketKey,
        business_id: pocketBusinessId,
        wallet_id: pocketWalletId,
        amount: Math.round(totalAmount * currency.factor),
        reference: reference,
        narration: `Voucher purchase for ${selectedPackages.length} package(s)`,
        view: "popup",
        account_expiry: 60,
        meta: {
          purpose: "voucher",
          business_id: selectedSalon?.id,
          payment_type: "full_payment",
          processing_fee_amount: responseData.processingFeeAmount ?? 0,
          reference: responseData.paymentAuthorization?.reference,
          discount_amount: responseData.discountAmount ?? 0,
          subtotal: responseData.subtotal ?? 0,
          tax_amount: responseData.taxAmount ?? 0,
          description: packageNames,
        },
      };
      
      // Add callbacks
      Object.assign(pocketConfig, {
        onSuccess: (response: any) => {
          handlePaymentSuccess(response.reference);
        },
        onClose: () => {
          handlePaymentClose();
        },
        onPending: () => {
          addToast({
            message:
              "Payment is being processed. Please wait for confirmation.",
            variant: "warning",
          });
        },
        onError: (error: any) => {
          addToast({
            message: error?.message || "Payment failed. Please try again.",
            variant: "error",
          });
          setLoading(false);
        },
      });

      const payWithPocket = new Pocket(pocketConfig);
      payWithPocket.show();
    } catch (error) {
      addToast({
        message: "Failed to initiate payment. Please try again.",
        variant: "error",
      });
      setLoading(false);
    }
  };

  const initiateStripePayment = (responseData: any) => {
    const paymentAuth = responseData.paymentAuthorization;
    if (!paymentAuth?.clientSecret) {
      addToast({
        message: "Payment authorization failed. Please try again.",
        variant: "error",
      });
      setLoading(false);
      return;
    }

    if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
      addToast({
        message: "Payment system not configured. Please contact support.",
        variant: "error",
      });
      setLoading(false);
      return;
    }

    setStripePaymentReference(paymentAuth.reference || paymentAuth.accessCode || "stripe");
    setStripeClientSecret(paymentAuth.clientSecret);
    setShowStripeCheckout(true);
  };

  const handleStripeCheckoutComplete = () => {
    const reference = stripePaymentReference || "stripe";
    setShowStripeCheckout(false);
    setStripeClientSecret(null);
    setStripePaymentReference(null);
    handlePaymentSuccess(reference);
  };

  const handleStripeCheckoutClose = () => {
    setShowStripeCheckout(false);
    setStripeClientSecret(null);
    setStripePaymentReference(null);
    handlePaymentClose();
  };

  const initiatePayment = (responseData: any, provider: PaymentProvider, channels?: string[]) => {
    switch (provider) {
      case "paystack":
        initiatePaystackPayment(responseData, channels);
        break;
      case "pocket":
        initiatePocketPayment(responseData);
        break;
      case "stripe":
        initiateStripePayment(responseData);
        break;
      default:
        addToast({
          message: "Invalid payment provider selected",
          variant: "error",
        });
        setLoading(false);
    }
  };

  const handleSubmit = async (
    e: React.FormEvent,
    selectedProvider?: PaymentProvider,
    channels?: string[],
  ) => {
    e.preventDefault();

    // Use passed provider or fall back to state
    const providerToUse = selectedProvider || paymentProvider;

    if (!validateForm()) {
      addToast({
        message: "Please fill in all required fields correctly",
        variant: "error",
      });
      return;
    }

    if (!recipients.length) {
      addToast({
        message: "Please add at least one recipient",
        variant: "error",
      });
      return;
    }

    setLoading(true);

    try {
      // Create voucher purchase data structure matching tt.json
      // Group recipients by their packages and ensure proper structure
      const processedRecipients = recipients.map((recipient) => ({
        ...recipient,
        phone: recipient.phone.replace(/[^0-9]/g, ""), // Clean phone number
        packages: recipient.packages.map((pkg) => ({
          packageId: pkg.packageId,
          quantity: pkg.quantity,
        })),
      }));

      const voucherPurchaseData: VoucherPurchaseInput = {
        recipients: processedRecipients,
        salonId: selectedSalon?.id || "",
        paymentProvider: providerToUse, // Pass the selected payment provider
        ...(channels && { channels }), // Restrict Paystack to specific payment channels
      };

      // Store the purchase data (you can use this for API calls)

      const response = await createVoucherPurchase({
        variables: {
          input: voucherPurchaseData,
        },
      });

      if (response.data?.createVoucher) {
        const { data } = response.data.createVoucher;
        if (data?.paymentAuthorization) {
          initiatePayment(data, providerToUse, channels);
        }
      }
    } catch (error) {
      console.error("Error processing:", error);
      addToast({
        message: "An error occurred. Please try again.",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    handleNextStep(BookingStep.INITIAL);
  };

  // Handle provider selection from modal
  const handleProviderSelect = (option: PaymentOption) => {
    const config = getPaymentOptionConfig(option, paymentCountryCode);
    setPaymentProvider(config.provider);
    setShowProviderModal(false);

    // Check if the selected provider's SDK is loaded
    const isProviderReady =
      config.provider === "paystack"
        ? paystackLoaded || window.PaystackPop
        : config.provider === "pocket"
          ? pocketLoaded || (window as any).Pocket
          : !!import.meta.env.VITE_STRIPE_PUBLIC_KEY;

    if (!isProviderReady) {
      addToast({
        message:
          config.provider === "stripe"
            ? "Payment system not configured. Please contact support."
            : "Payment provider is still loading. Please try again.",
        variant: "error",
      });
      return;
    }

    // Process the voucher purchase with the selected provider (pass provider directly)
    const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
    handleSubmit(syntheticEvent, config.provider, config.channels);
  };

  // Helper functions for managing recipients
  const updateRecipient = (
    index: number,
    field: keyof VoucherRecipient,
    value: any,
  ) => {
    setRecipients((prev) =>
      prev.map((recipient, i) =>
        i === index ? { ...recipient, [field]: value } : recipient,
      ),
    );

    // Clear errors for this field
    if (formErrors[`recipient_${index}`]?.[field]) {
      setFormErrors((prev: any) => ({
        ...prev,
        [`recipient_${index}`]: {
          ...prev[`recipient_${index}`],
          [field]: "",
        },
      }));
    }
  };

  // Add new recipient for a specific package
  const addRecipientForPackage = (packageId: string) => {
    const pkg = selectedPackages.find((p) => p.id === packageId);
    if (!pkg) return;

    const packageRecipients = getRecipientsForPackage(packageId);
    const totalQuantity = pkg.quantity || 1;

    // Can't add more recipients than the total quantity
    if (packageRecipients.length >= totalQuantity) return;

    // Redistribute quantities
    const newNumRecipients = packageRecipients.length + 1;
    const quantityDistribution = distributeQuantity(
      totalQuantity,
      newNumRecipients,
    );

    // Update existing recipients' quantities
    const updatedRecipients = recipients.map((recipient, index) => {
      const packageIndex = packageRecipients.findIndex((r) => r === recipient);
      if (packageIndex !== -1) {
        return {
          ...recipient,
          packages: recipient.packages.map((pkg) =>
            pkg.packageId === packageId
              ? { ...pkg, quantity: quantityDistribution[packageIndex] }
              : pkg,
          ),
        };
      }
      return recipient;
    });

    // Add new recipient
    const newRecipient: VoucherRecipient = {
      firstName: "",
      lastName: "",
      email: "",
      countryCode: selectedSalon?.countryCode || "NG",
      callingCode: selectedSalon?.callingCode || "+234",
      phone: "",
      packages: [
        {
          packageId,
          quantity: quantityDistribution[newNumRecipients - 1],
        },
      ],
    };

    setRecipients([...updatedRecipients, newRecipient]);
  };

  // Phone search function for individual recipients
  const performSearch = async (cleanPhone: string, recipientIndex: number) => {
    if (!selectedSalon?.id) return;

    setSearchLoading((prev) => ({ ...prev, [recipientIndex]: true }));

    try {
      const { data } = await searchClientQuery({
        salonId: selectedSalon.id,
        phone: cleanPhone,
        countryCode: recipients[recipientIndex].countryCode,
      });

      const existingClient = data?.clientSearch;

      setRecipients((prev) =>
        prev.map((recipient, index) =>
          index === recipientIndex
            ? {
                ...recipient,
                firstName: existingClient?.firstName || "",
                lastName: existingClient?.lastName || "",
                email: existingClient?.email || "",
                phone: cleanPhone,
              }
            : recipient,
        ),
      );

      // Show personal info section for this recipient
      setShowPersonalInfo((prev) => ({ ...prev, [recipientIndex]: true }));

      if (existingClient) {
        addToast({
          message: `Welcome back, ${existingClient.firstName}!`,
          variant: "success",
        });
      }
    } catch (error) {
      // Still show personal info form even if search fails
      setShowPersonalInfo((prev) => ({ ...prev, [recipientIndex]: true }));
    } finally {
      setSearchLoading((prev) => ({ ...prev, [recipientIndex]: false }));
    }
  };

  const formatPhoneDisplay = (value: string) => {
    const cleanValue = value.replace(/[^0-9]/g, "");

    if (cleanValue.length <= 3) {
      return cleanValue;
    } else if (cleanValue.length <= 6) {
      return `${cleanValue.slice(0, 3)} ${cleanValue.slice(3)}`;
    } else if (cleanValue.length <= 10) {
      return `${cleanValue.slice(0, 3)} ${cleanValue.slice(
        3,
        6,
      )} ${cleanValue.slice(6)}`;
    } else {
      return `${cleanValue.slice(0, 4)} ${cleanValue.slice(
        4,
        7,
      )} ${cleanValue.slice(7, 11)}`;
    }
  };

  // Success Dialog Component
  const SuccessDialog = () => {
    if (!showSuccessDialog) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-grey-100">
            <h2 className="text-xl font-semibold text-grey-900">
              Purchase Successful!
            </h2>
            <button
              onClick={() => {
                setShowSuccessDialog(false);
                handleNextStep(BookingStep.INITIAL);
              }}
              className="p-2 hover:bg-grey-50 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-grey-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 text-center">
            {/* Circular Ticket Icon */}
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-green-300 rounded-full flex items-center justify-center">
                <Ticket className="h-8 w-8 text-white" />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-semibold text-grey-900 mb-2">
              Congratulations!
            </h3>

            {/* Subtitle */}
            <p className="text-lg text-grey-700 mb-4">
              Your voucher purchase was successful
            </p>

            {/* Email notification copy */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-300 text-left">
                  <p className="font-medium mb-1">Voucher details sent!</p>
                  <p>
                    We've sent the voucher details and redemption instructions
                    to the recipient's email address. They can use this voucher
                    to book services at {businessInfo?.name || "our salon"}.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <Button
              onClick={() => {
                setShowSuccessDialog(false);
                setRecipients([]);
                resetBooking();
                handleNextStep(BookingStep.LANDING);
              }}
              className="w-full bg-green-300 hover:bg-green-400 text-white h-12"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-grey-5 pb-24 lg:pb-0">
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Recipients List */}
          <div className="lg:col-span-2 space-y-6">
            {/* Group by Package */}
            {selectedPackages.map((packageData) => {
              const packageRecipients = getRecipientsForPackage(packageData.id);
              const totalQuantity = packageData.quantity || 1;
              const canAddMore = packageRecipients.length < totalQuantity;

              return (
                <div key={packageData.id} className="space-y-4">
                  {/* Package Header */}
                  <div className="bg-grey-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Ticket className="h-5 w-5 text-green-300" />
                        <h3 className="text-sm font-semibold text-grey-900">
                          {packageData.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                if (
                                  packageData.quantity &&
                                  packageData.quantity > 1
                                ) {
                                  modifyPackageInCart(packageData.id, "remove");
                                }
                              }}
                              disabled={
                                !packageData.quantity ||
                                packageData.quantity <= 1
                              }
                              className="w-6 h-6 rounded-full bg-grey-100 hover:bg-grey-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                            >
                              <Minus className="h-3.5 w-3.5 text-grey-700" />
                            </button>
                            <div className="min-w-[1.5rem] text-center font-semibold text-grey-900 text-sm">
                              {packageData.quantity}
                            </div>
                            <button
                              onClick={() =>
                                modifyPackageInCart(packageData.id, "add")
                              }
                              className="w-6 h-6 rounded-full bg-grey-100 hover:bg-grey-200 flex items-center justify-center transition-colors"
                            >
                              <Plus className="h-3.5 w-3.5 text-grey-700" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-grey-600 mt-1">
                      {packageData.description}
                    </p>
                  </div>

                  {packageRecipients.map((recipient, recipientIndex) => {
                    const globalIndex = recipients.findIndex(
                      (r) => r === recipient,
                    );
                    const pkg = recipient.packages.find(
                      (p) => p.packageId === packageData.id,
                    );
                    if (!pkg) return null;

                    return (
                      <div
                        key={globalIndex}
                        className="bg-white rounded-lg p-6 shadow-sm border border-grey-100"
                      >
                        {/* Recipient Header */}
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-base font-semibold text-grey-900">
                            Recipient {recipientIndex + 1}
                          </h4>
                          <div className="text-sm text-grey-600">
                            Quantity: {pkg.quantity}
                          </div>
                        </div>

                        {/* Recipient Details Form */}
                        <div className="space-y-4">
                          <div className="text-sm text-grey-600 mb-4">
                            {showPersonalInfo[globalIndex]
                              ? `Welcome back, ${recipient.firstName || ""}${
                                  recipient.lastName
                                    ? ` ${recipient.lastName}`
                                    : ""
                                }!`
                              : "Enter recipient details"}
                          </div>

                          {/* Phone Number - Always shown first */}
                          <div>
                            <label className="block text-sm font-medium text-grey-700 mb-2">
                              Phone Number *
                            </label>
                            <div className="flex gap-2 min-w-0">
                              <SearchableCountrySelect
                                value={recipient.countryCode}
                                onChange={(code, calling) => {
                                  updateRecipient(
                                    globalIndex,
                                    "countryCode",
                                    code,
                                  );
                                  updateRecipient(
                                    globalIndex,
                                    "callingCode",
                                    calling,
                                  );
                                }}
                                className="min-w-[120px] flex-shrink-0 px-3 py-2"
                              />
                              <input
                                type="tel"
                                value={formatPhoneDisplay(recipient.phone)}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /[^0-9]/g,
                                    "",
                                  );
                                  updateRecipient(globalIndex, "phone", value);

                                  // Auto-search when phone becomes valid
                                  if (value.length >= 10) {
                                    performSearch(value, globalIndex);
                                  } else {
                                    // Hide personal info if phone is too short
                                    setShowPersonalInfo((prev) => ({
                                      ...prev,
                                      [globalIndex]: false,
                                    }));
                                  }
                                }}
                                className={`flex-1 min-w-0 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                                  formErrors[`recipient_${globalIndex}`]?.phone
                                    ? "border-red-500"
                                    : "border-grey-100"
                                }`}
                                placeholder="801 234 5678"
                              />
                            </div>
                            {formErrors[`recipient_${globalIndex}`]?.phone && (
                              <p className="text-red-500 text-xs mt-1">
                                {formErrors[`recipient_${globalIndex}`].phone}
                              </p>
                            )}

                            {/* Search Loading Indicator */}
                            {searchLoading[globalIndex] && (
                              <div className="flex items-center gap-2 mt-2">
                                <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                                <span className="text-sm text-green-600">
                                  Searching for existing account...
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Personal Information - Only show after phone search */}
                          {showPersonalInfo[globalIndex] && (
                            <>
                              {/* First Name & Last Name */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-grey-700 mb-2">
                                    First Name *
                                  </label>
                                  <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
                                    <input
                                      type="text"
                                      value={recipient.firstName}
                                      onChange={(e) =>
                                        updateRecipient(
                                          globalIndex,
                                          "firstName",
                                          e.target.value,
                                        )
                                      }
                                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                                        formErrors[`recipient_${globalIndex}`]
                                          ?.firstName
                                          ? "border-red-500"
                                          : "border-grey-100"
                                      }`}
                                      placeholder="John"
                                    />
                                  </div>
                                  {formErrors[`recipient_${globalIndex}`]
                                    ?.firstName && (
                                    <p className="text-red-500 text-xs mt-1">
                                      {
                                        formErrors[`recipient_${globalIndex}`]
                                          .firstName
                                      }
                                    </p>
                                  )}
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-grey-700 mb-2">
                                    Last Name *
                                  </label>
                                  <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
                                    <input
                                      type="text"
                                      value={recipient.lastName}
                                      onChange={(e) =>
                                        updateRecipient(
                                          globalIndex,
                                          "lastName",
                                          e.target.value,
                                        )
                                      }
                                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                                        formErrors[`recipient_${globalIndex}`]
                                          ?.lastName
                                          ? "border-red-500"
                                          : "border-grey-100"
                                      }`}
                                      placeholder="Doe"
                                    />
                                  </div>
                                  {formErrors[`recipient_${globalIndex}`]
                                    ?.lastName && (
                                    <p className="text-red-500 text-xs mt-1">
                                      {
                                        formErrors[`recipient_${globalIndex}`]
                                          .lastName
                                      }
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Email */}
                              <div>
                                <label className="block text-sm font-medium text-grey-700 mb-2">
                                  Email Address *
                                </label>
                                <div className="relative">
                                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
                                  <input
                                    type="email"
                                    value={recipient.email}
                                    onChange={(e) =>
                                      updateRecipient(
                                        globalIndex,
                                        "email",
                                        e.target.value,
                                      )
                                    }
                                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                                      formErrors[`recipient_${globalIndex}`]
                                        ?.email
                                        ? "border-red-500"
                                        : "border-grey-100"
                                    }`}
                                    placeholder="john.doe@example.com"
                                  />
                                </div>
                                {formErrors[`recipient_${globalIndex}`]
                                  ?.email && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {
                                      formErrors[`recipient_${globalIndex}`]
                                        .email
                                    }
                                  </p>
                                )}
                              </div>
                            </>
                          )}

                          {/* Info Box - Only show if personal info is not shown */}
                          {!showPersonalInfo[globalIndex] && (
                            <div className="flex items-center gap-1">
                              <AlertCircle className="h-4 w-4 text-green-300 flex-shrink-0 " />
                              <div className="text-sm text-green-300">
                                <p className="font-normal">
                                  Enter recipient's phone number
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Add New Recipient Button */}
                  {canAddMore && totalQuantity > 1 && (
                    <div className="flex justify-center">
                      <Button
                        onClick={() => addRecipientForPackage(packageData.id)}
                        variant="outline"
                        className="rounded-full bg-green-300 text-white hover:bg-green-400 border-green-300 hover:border-green-400 px-6 py-2"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add new recipient
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <CartSummary
            title="Voucher Summary"
            showMembershipBanner={false}
            showButton={!isMobile}
            customButton={{
              text: "Purchase Voucher",
              onClick: () => setShowProviderModal(true),
              disabled:
                loading ||
                recipients.length === 0 ||
                recipients.some(
                  (r, idx) =>
                    !r.phone.trim() ||
                    (showPersonalInfo[idx] &&
                      (!r.firstName.trim() ||
                        !r.lastName.trim() ||
                        !r.email.trim())),
                ),
            }}
          />
        </div>
      </div>

      {/* Mobile Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 w-full bg-white border-t border-grey-100 p-4 lg:hidden">
        <div className="flex gap-3">
          <Button
            onClick={handleBack}
            variant="outline"
            className="flex-1 border-grey-100"
          >
            Back
          </Button>
          <Button
            onClick={() => {
              setShowProviderModal(true);
            }}
            disabled={
              loading ||
              recipients.length === 0 ||
              recipients.some(
                (r, idx) =>
                  !r.phone.trim() ||
                  (showPersonalInfo[idx] &&
                    (!r.firstName.trim() ||
                      !r.lastName.trim() ||
                      !r.email.trim())),
              )
            }
            className="flex-1 bg-green-300 hover:bg-green-400 text-white disabled:opacity-50"
          >
            {loading ? "Processing..." : "Purchase Voucher"}
          </Button>
        </div>
      </div>

      <PaymentProviderModal
        isOpen={showProviderModal}
        onClose={() => setShowProviderModal(false)}
        onSelectOption={handleProviderSelect}
        loading={loading}
        availableOptions={availablePaymentOptions}
      />
      <StripeCheckoutModal
        isOpen={showStripeCheckout}
        clientSecret={stripeClientSecret}
        onClose={handleStripeCheckoutClose}
        onComplete={handleStripeCheckoutComplete}
      />

      {/* Success Dialog */}
      <SuccessDialog />
    </div>
  );
}
